// Phase 3.10.1 — pure-function date sanity checks for the BatchModal form.
//
// Lives in its own file (rather than next to ``BatchModal``) so React
// Fast Refresh stays happy: the ``react-refresh/only-export-components``
// rule wants a module with a component to export ONLY that component.

/** Per-date sanity checks beyond ISO-format parsing.
 *
 *  Levels (Phase 4.5.1 tightened the rules after the smoke run where
 *  a today-dated batch hit ``rinex_not_available`` because IGS hadn't
 *  published the file yet):
 *
 *  * **today OR future** → hard error. RINEX for today's
 *    observations isn't published by IGS data centres yet — the
 *    fetcher would 404 across all 7 mirrors. (Pre-4.5.1 this was
 *    only an error for STRICTLY future dates; today / yesterday
 *    were warnings.)
 *  * **< 2000-01-01** → hard error (probable typo).
 *  * **today - 30d ≤ d < today** → soft warning. IGS final-orbit
 *    files for "fresh" dates may still be missing; the operator
 *    might want to wait or might have a known reason to proceed.
 *  * **< today - 30d** → no message. Final orbits are guaranteed
 *    available at this point.
 *
 *  Returned arrays are de-duplicated to avoid identical messages
 *  spamming the UI when multiple dates trip the same rule.
 */
export function validateObsDates(
  isoDates: readonly string[],
  now: Date = new Date(),
): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  // Truncate ``now`` to local midnight so we compare on calendar
  // dates, not wall-clock time.
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const minDate = new Date('2000-01-01T00:00:00');
  // Phase 4.5.1 — anything within the last 30 days gets the
  // soft "обратите внимание" warning. The 30-day cutoff matches
  // the practical IGS final-orbit guarantee.
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  let sawTodayOrFuture = false;
  let sawAncient = false;
  let sawRecent = false;

  for (const iso of isoDates) {
    const d = new Date(iso + 'T00:00:00');
    if (Number.isNaN(d.getTime())) continue; // parseDates already errored on format
    // Phase 4.5.1: today >= today is the hard error band now (was
    // strictly-greater-than; bumped to include today).
    if (d.getTime() >= today.getTime() && !sawTodayOrFuture) {
      errors.push(
        'Дата наблюдений не может быть сегодняшней или будущей. ' +
          'RINEX-файлы IGS публикуются после фактического дня наблюдения.',
      );
      sawTodayOrFuture = true;
    } else if (d.getTime() < minDate.getTime() && !sawAncient) {
      errors.push('Дата раньше 2000-01-01. Возможно опечатка.');
      sawAncient = true;
    } else if (d.getTime() >= thirtyDaysAgo.getTime() && !sawRecent) {
      // Phase 4.5.1 — recent (but past) dates: yellow warning, no
      // block. d is strictly less than today (caught above) and ≥
      // 30 days ago.
      warnings.push(
        'Обратите внимание на дату наблюдений: RINEX для свежих ' +
          'дат (≤ 30 дней) может быть ещё не опубликован в архивах. ' +
          'Можете продолжить, если уверены.',
      );
      sawRecent = true;
    }
  }

  return { errors, warnings };
}
